import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { messageApi } from '@/services/api'
import { useAuth } from '@/hooks/useAuth'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import { formatRelativeTime } from '@/utils/format'
import { Send, MessageSquare, Search, X, Lock } from 'lucide-react'

interface ChannelMessage {
  id: number
  channel: string
  user_id: number
  user_name: string
  first_name: string
  last_name: string
  message: string
  is_private: number | boolean
  recipient_id: number | null
  recipient_name: string | null
  recipient_first_name: string | null
  recipient_last_name: string | null
  created_at: string
}

interface ChannelUser {
  user_id: number
  user_name: string
  message_count: number
}

export default function Messages() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [message, setMessage] = useState('')
  const [selectedChannel, setSelectedChannel] = useState('general')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRecipient, setSelectedRecipient] = useState<{ id: number; name: string } | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const { data: messagesData, isLoading } = useQuery({
    queryKey: ['channel-messages', selectedChannel],
    queryFn: () => messageApi.getChannel(selectedChannel),
    refetchInterval: () => {
      // Only poll when window is focused to reduce unnecessary refreshes
      return document.hasFocus() ? 5000 : false
    },
  })

  const sendMessageMutation = useMutation({
    mutationFn: ({ msg, recipientId }: { msg: string; recipientId?: number }) =>
      messageApi.sendChannel(selectedChannel, msg, recipientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel-messages', selectedChannel] })
      setMessage('')
      setSelectedRecipient(null)
    },
  })

  const messages: ChannelMessage[] = (messagesData?.data as any)?.messages || []

  // Get unique users in the current channel with message counts (excluding current user)
  const channelUsers: ChannelUser[] = Array.from(
    messages
      .filter((msg) => msg.user_id !== user?.id)
      .reduce((acc, msg) => {
        const existing = acc.get(msg.user_id)
        if (existing) {
          existing.message_count++
        } else {
          acc.set(msg.user_id, {
            user_id: msg.user_id,
            user_name: msg.user_name,
            message_count: 1,
          })
        }
        return acc
      }, new Map<number, ChannelUser>())
      .values()
  )

  // Sort by message count and get top 10 most active
  const topActiveUsers = [...channelUsers]
    .sort((a, b) => b.message_count - a.message_count)
    .slice(0, 10)

  // Filter users based on search term (search all users, not just top 10)
  const filteredUsers = searchTerm
    ? channelUsers.filter((channelUser) =>
        channelUser.user_name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : topActiveUsers

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim()) return
    sendMessageMutation.mutate({
      msg: message,
      recipientId: selectedRecipient?.id,
    })
  }

  const handleSelectRecipient = (userId: number, userName: string) => {
    setSelectedRecipient({ id: userId, name: userName })
    setSearchTerm('')
  }

  const handleClearRecipient = () => {
    setSelectedRecipient(null)
  }

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Reset recipient when changing channels
  useEffect(() => {
    setSelectedRecipient(null)
    setSearchTerm('')
  }, [selectedChannel])

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Messages</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Channel List */}
        <Card title="Channels" className="lg:col-span-1">
          <div className="space-y-2">
            {['general', 'support', 'trading'].map((channel) => (
              <button
                key={channel}
                onClick={() => setSelectedChannel(channel)}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                  selectedChannel === channel
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <MessageSquare className="w-4 h-4" />
                  <span className="font-medium capitalize">{channel}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Active Users in Channel with Search */}
          {channelUsers.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                {searchTerm ? 'Search Results' : 'Top 10 Most Active'}
              </h3>

              {/* Search Input */}
              <div className="relative mb-2">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search members..."
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* User List */}
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {filteredUsers.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-2">
                    {searchTerm ? 'No users found' : 'No active users yet'}
                  </p>
                ) : (
                  filteredUsers.map((channelUser) => (
                    <div
                      key={channelUser.user_id}
                      className="flex items-center justify-between px-2 py-1 hover:bg-gray-100 rounded"
                    >
                      <button
                        onClick={() => navigate(`/dashboard/users/${channelUser.user_id}`)}
                        className="text-sm text-gray-700 hover:text-primary-600 hover:underline flex-1 text-left truncate"
                      >
                        {channelUser.user_name}
                      </button>
                      <button
                        onClick={() => handleSelectRecipient(channelUser.user_id, channelUser.user_name)}
                        className={`ml-2 px-2 py-1 text-xs rounded whitespace-nowrap ${
                          selectedRecipient?.id === channelUser.user_id
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-primary-100'
                        }`}
                      >
                        {selectedRecipient?.id === channelUser.user_id ? 'Selected' : 'Select'}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Message Area */}
        <div className="lg:col-span-3">
          <Card title={`#${selectedChannel}`}>
            <div className="space-y-4">
              {/* Selected Recipient Banner */}
              {selectedRecipient && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Lock className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">
                      Sending private message to: <strong>{selectedRecipient.name}</strong>
                    </span>
                  </div>
                  <button
                    onClick={handleClearRecipient}
                    className="text-amber-600 hover:text-amber-800"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Messages */}
              <div className="h-[500px] overflow-y-auto border rounded-lg p-4 bg-gray-50 space-y-3">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500">Loading messages...</p>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500 text-center">
                      No messages yet. Start the conversation!
                    </p>
                  </div>
                ) : (
                  <>
                    {messages.map((msg) => {
                      const isMyMessage = msg.user_id === user?.id
                      const isPrivate = Boolean(msg.is_private)
                      const isSender = msg.user_id === user?.id && isPrivate

                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-md lg:max-w-lg px-4 py-3 rounded-lg ${
                              isPrivate
                                ? isMyMessage
                                  ? 'bg-amber-600 text-white border-2 border-amber-700'
                                  : 'bg-amber-100 text-gray-900 border-2 border-amber-300'
                                : isMyMessage
                                ? 'bg-primary-600 text-white'
                                : 'bg-white text-gray-900 border border-gray-200'
                            }`}
                          >
                            {/* Private Message Indicator */}
                            {isPrivate && (
                              <div className="flex items-center space-x-1 mb-1">
                                <Lock className="w-3 h-3" />
                                <span className={`text-xs font-semibold ${
                                  isMyMessage ? 'text-amber-100' : 'text-amber-700'
                                }`}>
                                  {isSender ? `Private to ${msg.recipient_name}` : `Private from ${msg.user_name}`}
                                </span>
                              </div>
                            )}

                            <div className="flex items-baseline space-x-2 mb-1">
                              {isMyMessage ? (
                                <span
                                  className={`text-xs font-semibold ${
                                    isPrivate ? 'text-amber-100' : 'text-primary-100'
                                  }`}
                                >
                                  You
                                </span>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    navigate(`/dashboard/users/${msg.user_id}`)
                                  }}
                                  className={`text-xs font-semibold hover:underline ${
                                    isPrivate ? 'text-amber-700' : 'text-primary-600'
                                  }`}
                                >
                                  {msg.user_name}
                                </button>
                              )}
                              <span
                                className={`text-xs ${
                                  isPrivate
                                    ? isMyMessage
                                      ? 'text-amber-200'
                                      : 'text-amber-600'
                                    : isMyMessage
                                    ? 'text-primary-200'
                                    : 'text-gray-500'
                                }`}
                              >
                                {formatRelativeTime(msg.created_at)}
                              </span>
                            </div>
                            <p className="text-sm break-words">{msg.message}</p>
                          </div>
                        </div>
                      )
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="flex space-x-2">
                <input
                  type="text"
                  className={`input flex-1 ${
                    selectedRecipient ? 'border-amber-400 focus:ring-amber-500' : ''
                  }`}
                  placeholder={
                    selectedRecipient
                      ? `Private message to ${selectedRecipient.name}...`
                      : `Message #${selectedChannel}...`
                  }
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
                <Button
                  type="submit"
                  disabled={!message.trim()}
                  isLoading={sendMessageMutation.isPending}
                  className={selectedRecipient ? 'bg-amber-600 hover:bg-amber-700' : ''}
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
